import { useFormik } from "formik";
import React, { Fragment, useEffect, useMemo } from "react";
import * as yup from "yup";
import { useCopilotContext } from "@copilotkit/react-core";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import moment from "moment";

import { ForecastNewProductFormikValues } from "./types/ForecastNewProductStepForm";
import ForecastNewProductEnable from "./ForecastNewProductEnable";
import ForecastNewProductPlan from "./ForecastNewProductPlan";
import ForecastNewProductPlanLoader from "./ForecastNewProductPlanLoader";
import ForecastNewProductPlanResult from "./ForecastNewProductPlanResult";
import ForecastNewProductPlanEdit from "./ForecastNewProductPlanEdit";
import ForecastNewProductPlanList from "./ForecastNewProductPlanList";
import { FORECAST_NEW_PRODUCT_STEP } from "./constant/ForecastNewProductStepConstant";
import { ModalContainerProps } from "components/common/ModalContainer";
import useGetAllAgent from "hooks/workforceHooks/useGetAllAgent";
import { AgentType } from "enums/AgentTypeEnum";
import {
    useCancelNewProductPlan,
    useCreateNewProductPlan,
    useGetNewProductPlans,
    useGetSingleNewProductPlan,
    useUpdateNewProductPlan,
} from "hooks/newProductPlanHooks";
import useUpdateAgentStatus from "hooks/workforceHooks/useUpdateAgentStatus";
import Loader from "components/common/Loader";
import useStepper from "hooks/useStepper";
import CustomToast from "components/common/CustomToast";
import useAddForecastConfig from "hooks/inventoryHooks/useAddForecastConfig";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { chatActions } from "redux/Ldb/actions";
import ForecastNewProductPlanProgressModal from "./ForecastNewProductPlanProgressModal";

type ForecastNewProductModalProps = {
    selectedSimilarProducts?: {
        _id: string;
        productName: string;
        sku: string;
    }[];
    step?: number;
} & Omit<ModalContainerProps, "children">;

export default function ForecastNewProductModal(props: ForecastNewProductModalProps) {
    const { selectedSimilarProducts, step, ...rest } = props;

    const dispatch = useAppDispatch();
    const { addForecastConfigSuccess } = useAppSelector((state) => state.dashboard);
    const { updateAgentStatusSuccess } = useAppSelector((state) => state.workforce);

    const { setThreadId, setAgentSession } = useCopilotContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const newProductPlanId = searchParams.get("newProductPlanId");

    const getAllAgentsQuery = useGetAllAgent({
        agentType: AgentType.NewProductForecaster,
    });

    const [createNewProductPlanMutation] = useCreateNewProductPlan();
    const [updateNewProductPlanMutation] = useUpdateNewProductPlan();
    const [updateAgentStatusMutation] = useUpdateAgentStatus();
    const [cancelNewProductPlanMutation] = useCancelNewProductPlan();
    const [addForecastConfigMutation] = useAddForecastConfig();

    const productForecastAgent = getAllAgentsQuery.data?.find(
        (agent) => agent.type === AgentType.NewProductForecaster
    );
    const activeAiAgent = productForecastAgent?.activeAgent?.isActive || false;
    const productForecastAgentId = productForecastAgent?._id;

    const stepper = useStepper(
        useMemo(
            () => ({
                initialStep:
                    step ||
                    (activeAiAgent
                        ? FORECAST_NEW_PRODUCT_STEP.PLAN
                        : FORECAST_NEW_PRODUCT_STEP.ENABLE),
            }),
            [activeAiAgent, step]
        )
    );

    const getSingleNewProductPlanQuery = useGetSingleNewProductPlan(
        {
            id: newProductPlanId,
        },
        {
            skip:
                (!newProductPlanId && stepper?.step !== FORECAST_NEW_PRODUCT_STEP.PLAN_EDIT) ||
                !newProductPlanId ||
                stepper?.step !== FORECAST_NEW_PRODUCT_STEP.PLAN_EDIT,
        }
    );
    const newProductPlan = getSingleNewProductPlanQuery?.data?.data;

    const getNewProductPlanListQuery = useGetNewProductPlans(
        useMemo(
            () => ({
                status: "InProgress",
            }),
            [stepper.step]
        ),
        useMemo(
            () => ({
                skip: stepper.step !== FORECAST_NEW_PRODUCT_STEP.PLAN_PROGRESS,
                refetchOnMount: true,
                // pollingInterval: 10000, //10 seconds
            }),
            [stepper.step]
        )
    );

    const productsInprogress = getNewProductPlanListQuery?.data;

    const formik = useFormik<ForecastNewProductFormikValues>({
        initialValues: {
            newProductPlanId: newProductPlanId || "",
            enableAgent: activeAiAgent,
            productId:
                (newProductPlan?.productId &&
                    `${newProductPlan?.productId}-${newProductPlan?.productDetails?.productName}`) ||
                "",
            launchDate: newProductPlan?.launchDate
                ? moment(newProductPlan?.launchDate).format("YYYY-MM-DD")
                : "",
            projectedGrowthRate: newProductPlan?.growthRate || 0,
            additionalDetails: newProductPlan?.additionalDetails || "",
            productChannel: newProductPlan?.productChannel || "",
            similarProducts:
                stepper.step === FORECAST_NEW_PRODUCT_STEP.PLAN_PROGRESS &&
                productsInprogress?.length > 0
                    ? productsInprogress?.map((product) => {
                          const productDetails = product?.productDetails?.[0];
                          return {
                              _id: product?._id,
                              channel: product?.productChannel,
                              productSku: productDetails?.sku,
                              ldbId: product?.ldbId,
                              productCategory: productDetails?.inventoryCategory,
                              productId: `${productDetails?._id}-${productDetails?.productName}`,
                              forecastStartDate: product?.forecastStartDate,
                              forecastEndDate: product?.forecastEndDate,
                              marketingAggressiveness:
                                  product?.marketingAggressiveness?.description,
                              targetAudience: product?.targetAudience?.description,
                              relativePriceTier: product?.relativePriceTier?.description,
                              progress: product?.progress || 0,
                          };
                      })
                    : stepper.step !== FORECAST_NEW_PRODUCT_STEP.PLAN_PROGRESS &&
                      newProductPlan?.similarProducts?.length > 0
                    ? newProductPlan?.similarProducts?.map?.((product) => {
                          return {
                              _id: product?._id,
                              channel: product?.productChannel,
                              productSku: product?.sku,
                              productCategory: product?.inventoryCategory,
                              productId: `${product?.product?._id}-${product?.product?.productName}`,
                              forecastStartDate: product?.forecastStartDate,
                              forecastEndDate: product?.forecastEndDate,
                              marketingAggressiveness:
                                  product?.marketingAggressiveness?.description,
                              targetAudience: product?.targetAudience?.description,
                              relativePriceTier: product?.relativePriceTier?.description,
                              progress: product?.progress || 0,
                          };
                      })
                    : [
                          ...(selectedSimilarProducts?.length > 0
                              ? selectedSimilarProducts?.map((product) => ({
                                    productChannel: "",
                                    productSku: product?.sku,
                                    productId: `${product?._id}-${product?.productName}`,
                                    forecastStartDate: "",
                                    forecastEndDate: "",
                                    marketingAggressiveness: "",
                                    targetAudience: "",
                                    relativePriceTier: "",
                                }))
                              : [
                                    {
                                        productChannel: "",
                                        productId: "",
                                        forecastStartDate: "",
                                        forecastEndDate: "",
                                        marketingAggressiveness: "",
                                        targetAudience: "",
                                        relativePriceTier: "",
                                    },
                                ]),
                      ],
        },
        validationSchema: yup.object({
            ...[
                {
                    enableAgent: yup.boolean().label("Enable Agent").required("Required"),
                },
                {
                    productChannel: yup.string().label("Product Channel").required("Required"),
                    productId: yup.string().label("Product ID").required("Required"),
                    launchDate: yup.string().label("Launch Date").required("Required"),
                    projectedGrowthRate: yup.number().label("Growth Rate").required("Required"),
                    additionalDetails: yup.string().label("Additional Details").required(),
                    similarProducts: yup
                        .array()
                        .of(
                            yup.object({
                                channel: yup.string().label("Channel").required("Required"),
                                productId: yup.string().label("Product ID").required("Required"),
                                forecastStartDate: yup
                                    .string()
                                    .label("Forecast Period")
                                    .required("Required"),
                                forecastEndDate: yup
                                    .string()
                                    .label("Forecast Period")
                                    .required("Required"),
                                marketingAggressiveness: yup.string(),
                                targetAudience: yup.string(),
                                relativePriceTier: yup.string(),
                            })
                        )
                        .min(1, "At least one similar product is required"),
                },
                {},
                {},
                {},
            ][stepper.step],
        }),
        enableReinitialize: true,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        onSubmit: async (values, { setFormikState, resetForm }) => {
            try {
                switch (stepper.step) {
                    case FORECAST_NEW_PRODUCT_STEP.ENABLE:
                        await updateAgentStatusMutation(productForecastAgentId, {
                            isActive: values?.enableAgent,
                        });
                        setSearchParams({});
                        stepper.next();
                        break;
                    case FORECAST_NEW_PRODUCT_STEP.PLAN:
                        const createNewProductPlanMutationResponse =
                            await createNewProductPlanMutation({
                                productId: values?.productId?.split("-")[0] || values?.productId,
                                launchDate: values?.launchDate,
                                growthRate: values?.projectedGrowthRate,
                                additionalDetails: values?.additionalDetails || "",
                                productChannel: values?.productChannel || "",
                                similarProducts: values?.similarProducts.map((similarProduct) => {
                                    return {
                                        productChannel: similarProduct?.channel,
                                        productId:
                                            similarProduct?.productId?.split("-")[0] ||
                                            similarProduct?.productId,
                                        forecastStartDate: similarProduct?.forecastEndDate,
                                        forecastEndDate: similarProduct?.forecastEndDate,
                                        marketingAggressivenessDescription:
                                            similarProduct?.marketingAggressiveness || "",
                                        targetAudienceDescription:
                                            similarProduct?.targetAudience || "",
                                        relativePriceTierDescription:
                                            similarProduct?.relativePriceTier || "",
                                    };
                                }),
                            });
                        setSearchParams({
                            runType: "newRun",
                        });
                        setThreadId(createNewProductPlanMutationResponse?.data?.threadId);
                        setAgentSession({
                            agentName: AgentType.NewProductForecaster,
                            threadId: createNewProductPlanMutationResponse?.data?.threadId,
                        });

                        setFormikState((prevState) => {
                            return {
                                ...prevState,
                                values: {
                                    ...values,
                                    newProductPlanId:
                                        createNewProductPlanMutationResponse?.data?._id,
                                    threadId: createNewProductPlanMutationResponse?.data?.threadId,
                                    ldbId: createNewProductPlanMutationResponse?.data?.ldbId,
                                },
                            };
                        });

                        stepper.next();
                        break;
                    case FORECAST_NEW_PRODUCT_STEP.PLAN_LOADER:
                        await cancelNewProductPlanMutation(values.newProductPlanId);
                        resetForm();
                        setFormikState((prevState) => {
                            return {
                                ...prevState,
                                values: {
                                    ...prevState.values,
                                    newProductPlanId: "",
                                    threadId: "",
                                    ldbId: "",
                                },
                            };
                        });
                        setSearchParams({});
                        stepper.go(FORECAST_NEW_PRODUCT_STEP.PLAN);
                        setThreadId("");
                        rest?.closeModal();
                        break;
                    case FORECAST_NEW_PRODUCT_STEP.PLAN_RESULT:
                        await addForecastConfigMutation({
                            configs: [
                                ...values?.salesData.map((sales) => ({
                                    ...sales,
                                    channel: values.productChannel as any,
                                    inventoryId:
                                        values?.productId?.split("-")[0] || values?.productId,
                                    // forecastMethod: undefined,
                                })),
                            ],
                        });
                        rest?.closeModal();
                        setThreadId("");
                        setSearchParams({});
                        rest?.closeModal();
                        break;
                    case FORECAST_NEW_PRODUCT_STEP.PLAN_EDIT:
                        const allSimilarProducts = [...values?.similarProducts];

                        const updateNewProductPlanMutationResponse =
                            await updateNewProductPlanMutation({
                                productPlanId: values?.newProductPlanId,
                                productId: values?.productId?.split("-")[0] || values?.productId,
                                launchDate: values?.launchDate,
                                growthRate: values?.projectedGrowthRate,
                                additionalDetails: values?.additionalDetails || "",
                                productChannel: values?.productChannel || "",
                                similarProducts: allSimilarProducts.map((similarProduct) => {
                                    return {
                                        ...(similarProduct?._id
                                            ? { _id: similarProduct?._id }
                                            : {}),
                                        productChannel: similarProduct?.channel,
                                        productId:
                                            similarProduct?.productId?.split("-")[0] ||
                                            similarProduct?.productId,
                                        forecastStartDate: similarProduct?.forecastStartDate,
                                        forecastEndDate: similarProduct?.forecastStartDate,
                                        marketingAggressivenessDescription:
                                            similarProduct?.marketingAggressiveness || "",
                                        targetAudienceDescription:
                                            similarProduct?.targetAudience || "",
                                        relativePriceTierDescription:
                                            similarProduct?.relativePriceTier || "",
                                    };
                                }),
                            });
                        setThreadId(updateNewProductPlanMutationResponse?.data?.threadId);
                        setFormikState((prevState) => {
                            return {
                                ...prevState,
                                values: {
                                    ...values,
                                    newProductPlanId:
                                        updateNewProductPlanMutationResponse?.data?._id ||
                                        values?.newProductPlanId,
                                    threadId: updateNewProductPlanMutationResponse?.data?.threadId,
                                    ldbId: updateNewProductPlanMutationResponse?.data?.ldbId,
                                },
                            };
                        });
                        setSearchParams({
                            runType: "rerun",
                            newProductPlanId:
                                updateNewProductPlanMutationResponse?.data?._id ||
                                values?.newProductPlanId,
                        });

                        stepper.go(FORECAST_NEW_PRODUCT_STEP.PLAN_LOADER);
                        break;
                    case FORECAST_NEW_PRODUCT_STEP.PLAN_LIST:
                        break;
                    case FORECAST_NEW_PRODUCT_STEP.PLAN_PROGRESS:
                        for (const similarProduct of values?.similarProducts) {
                            await cancelNewProductPlanMutation(similarProduct?._id);
                        }
                        // rest?.closeModal();
                        break;
                }
            } catch (error) {
                console.log({ error });
                toast.custom((toastRef) => (
                    <CustomToast
                        t={toastRef}
                        message={error?.message || "Something went wrong"}
                        type="error"
                    />
                ));
            }
        },
    });

    const contentProps = {
        formik,
        stepper,
        isLoading: getSingleNewProductPlanQuery?.isLoading || getNewProductPlanListQuery?.isLoading,
        refetch: getNewProductPlanListQuery?.refetch,
        tailwindClassName: "w-full bg-white !rounded-[8px] border border-[#F1F5F9]",
        ...rest,
        closeModal: () => {
            rest.closeModal();
            setThreadId("");
            setSearchParams({});
            formik.resetForm();
            dispatch(chatActions.setAgentType("synth-gpt"));
        },
    };

    const steps = [
        {
            title: "Enable Required AI Agent",
            content: <ForecastNewProductEnable {...contentProps} />,
        },
        {
            title: "Plan forecast for a new product",
            content: <ForecastNewProductPlan {...contentProps} />,
        },
        {
            title: "Planning your forecast...",
            content: <ForecastNewProductPlanLoader {...contentProps} />,
        },
        {
            title: "Your forecast is ready",
            content: <ForecastNewProductPlanResult {...contentProps} />,
        },
        {
            title: "Edit your forecast",
            content: <ForecastNewProductPlanEdit {...contentProps} />,
        },
        {
            title: "All planned products",
            content: <ForecastNewProductPlanList {...contentProps} />,
        },
        {
            title: "New product forecast in progress",
            content: <ForecastNewProductPlanProgressModal {...contentProps} />,
        },
    ];

    // TODO: properly handle the success and failure of the addForecastConfigMutation
    useEffect(() => {
        dispatch(chatActions.setAgentType(AgentType.NewProductForecaster));

        if (!!addForecastConfigSuccess) {
            rest?.closeModal();
            setThreadId("");
            setSearchParams({});
            formik.resetForm();
        }
    }, [addForecastConfigSuccess]);

    useEffect(() => {
        if (updateAgentStatusSuccess) {
            stepper.next();
        }
    }, [updateAgentStatusSuccess]);

    return (
        <Fragment>
            {getAllAgentsQuery.isFetching ? (
                <div className="h-screen z-[1000000000] w-screen absolute top-0 left-0 bg-[rgba(0,0,0,0.1)] flex items-center justify-center">
                    <Loader />
                </div>
            ) : (
                steps[stepper.step].content
            )}
        </Fragment>
    );
}
